import { useEffect, useState , createContext } from 'react';
import { generateClient } from 'aws-amplify/data';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import outputs from '../../amplify_outputs.json';
import { SortDirection } from 'aws-amplify/datastore';
import { uploadData, getUrl } from 'aws-amplify/storage';

Amplify.configure(outputs);

const BASE_FREE_DRAW_MAX_PIXELS = 25;
const GUEST_FREE_DRAW_MAX_PIXELS = 5;
const BLOCKED_CANVAS_PAGE_TERMS = [
  'asshole',
  'bastard',
  'bitch',
  'bullshit',
  'cock',
  'cunt',
  'dick',
  'douche',
  'fag',
  'faggot',
  'fuck',
  'motherfucker',
  'nigger',
  'penis',
  'pussy',
  'shit',
  'slut',
  'twat',
  'whore',
];

const normalizeCanvasPageText = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const containsBlockedCanvasPageTerm = (value = '') => {
  const normalized = normalizeCanvasPageText(value);
  if (!normalized) return false;

  const compact = normalized.replace(/\s+/g, '');
  return BLOCKED_CANVAS_PAGE_TERMS.some((term) => {
    const normalizedTerm = normalizeCanvasPageText(term).replace(/\s+/g, '');
    return normalized.includes(term) || compact.includes(normalizedTerm);
  });
};

const normalizeCanvasPageName = (value = '') => String(value || '').trim().toUpperCase();

const clientUser = generateClient({ authMode: 'userPool' });
const clientGuest = generateClient({ authMode: 'identityPool' });

export const BackendContext = createContext();

export const BackendProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [userProfile, setUserProfile] = useState(null);
  const [userTiles, setUserTiles] = useState(null);
  const [userArtwork, setUserArtwork] = useState(null);
  const [canvas, setCanvas] = useState(null);
  const [canvasUrl, setCanvasUrl] = useState(null);
  const [chunks, setChunks] = useState([]); 
  const [currentEvent, setCurrentEvent] = useState(null);
  const [eventCanvas, setEventCanvas] = useState(null);

  const [client, setClient] = useState(null);


  const canvasSubscribers = new Set();
  const chatSubscribers = new Set();
  
  let pageToken = null;
  let tempTile = [];

  const fetchClientMode = async () => {
    try {
      await getCurrentUser();           // resolves when signed in
      //console.log('[Data] using userPool: ', clientUser);
      setClient(clientUser);
      return clientUser;
    } catch {
      //console.log('[Data] using apiKey (guest): ', clientGuest);
      setClient(clientGuest);
      return clientGuest;
    }
  }

  useEffect(() => {
    async function fetchClientMode() {
      try {
        await getCurrentUser();           // resolves when signed in
        //console.log('[Data] using userPool');
        setClient(clientUser);
      } catch {
        //console.log('[Data] using apiKey (guest)');
        setClient(clientGuest);
      }
    }

    fetchClientMode();
  }, []);

  // #region Canvas Subscriptions
  const subscribeToCanvasArtworkUpdates = async () => {
    //console.log("*subscribeToCanvasArtworkUpdates* Subscribing to canvas artwork updates");
    const updateSub = client.models.Artwork.onUpdate({
      filter: {
        page: { eq: canvas?.page }, //canvas?.page
      }
    }).subscribe({
        next: (data) => updateLocalCanvas(data, 'Artwork'),
        error: (error) => console.warn(error),
        owner: fetchUserAttributes.subscribe,
        authMode: 'identityPool'
      })

      return () => updateSub.unsubscribe();
      // eslint-disable-next-line
  };

  const subscribeToCanvasArtworkMaskChunkUpdates = async () => {
    //console.log("*subscribeToCanvasArtworkMaskChunkUpdates* Subscribing to canvas ArtworkMaskChunk updates");
    const updateSub = client.models.ArtworkMaskChunk.onUpdate({
      filter: {
        page: { eq: canvas?.page }, //canvas?.page
      }
    }).subscribe({
        next: (data) => updateLocalCanvas(data, 'ArtworkMaskChunk'),
        error: (error) => console.warn(error),
        owner: fetchUserAttributes.subscribe,
        authMode: 'identityPool'
      })

      return () => updateSub.unsubscribe();
      // eslint-disable-next-line
  };

  const subscribeToFreeCanvasChunkUpdates = async ({ page } = {}) => {
    const activeClient = await fetchClientMode();
    const matchesPage = (evt) => {
      const rec = evt?.data ?? evt;
      return !!rec && (!page || rec.page === page);
    };

    const createSub = activeClient.models.FreeCanvasChunk.onCreate().subscribe({
      next: (evt) => {
        if (matchesPage(evt)) updateLocalCanvas(evt, 'FreeCanvasChunk');
      },
      error: (error) => console.warn(error),
      owner: fetchUserAttributes.subscribe,
      authMode: 'identityPool',
    });

    const updateSub = activeClient.models.FreeCanvasChunk.onUpdate().subscribe({
      next: (evt) => {
        if (matchesPage(evt)) updateLocalCanvas(evt, 'FreeCanvasChunk');
      },
      error: (error) => console.warn(error),
      owner: fetchUserAttributes.subscribe,
      authMode: 'identityPool',
    });

    return () => {
      try { createSub.unsubscribe(); } catch {}
      try { updateSub.unsubscribe(); } catch {}
    };
  };

  const subscribeToCanvasUpdatesLocal = (callback) => {
    canvasSubscribers.add(callback);
    return () => canvasSubscribers.delete(callback); // unsubscribe cleanup
  };

  const updateLocalCanvas = (data, kind) => {
    //Send a notification to the canvas with the tile row/column that got updated to update it locally
    const record = data.data ?? data; // Amplify subs usually put the row on .data
    notifySubscribers({ kind, record });
  }

  const notifySubscribers = (updateGrid) => {
    canvasSubscribers.forEach((cb) => cb(updateGrid));
  }
  // #endregion Canvas Subscriptions

  // #region User Profile Management
  const fetchUserProfile = async () => {
    //console.log("*fetchUserProfile*🟡 Starting user fetch");
    setIsUserLoading(true);
    const activeClient = await fetchClientMode();
    try {
      // 1) Make sure the Hosted UI redirect has been finalized
      const session = await fetchAuthSession();
      const isSignedIn = !!session?.tokens?.idToken;
      const user = await getCurrentUser().catch(() => null);

      if (!isSignedIn || !user) {
        //console.log("🔴 No signed-in user yet");
        setUserProfile(null);
        setIsUserLoading(false);
        return;
      }
      
      const sub = user.userId;

      const { data: updatedUserProfile, error } = await activeClient.models.UserProfile.get({
        sub,
      });

      if (error || !updatedUserProfile) {
        //console.log("🔴 Failed to fetch profile", error);
        setUserProfile(null);
        setIsUserLoading(false);
        return;
      }

      //console.log("*fetchUserProfile*🟢 Fetched user profile:", updatedUserProfile);
      setUserProfile(updatedUserProfile);
      //fetchUserProfileAvatar(updatedUserProfile.username);

      // Check if user is admin
      const groups = session.tokens.idToken.payload?.["cognito:groups"] || [];
      setIsAdmin(Array.isArray(groups) && groups.includes("ADMINS"));
      //console.log("*fetchUserProfile*", groups.includes("ADMINS") ? "User is in ADMINS" : "User is not in ADMINS");

    } catch (e) {
      //console.log("*fetchUserProfile*🔴 Failed to fetch profile", e)
      setUserProfile(null);
    } finally {
      setIsUserLoading(false);
    }
  };

  const clearUserState = () => {
    setUserProfile(null);
    setIsAdmin(false);
    setIsUserLoading(false);
    setUserTiles(null);
    setUserArtwork(null);
  }

  const fetchSpecificUserProfile = async (userSub) => {
    try {
      const { data, errors } = await client.models.UserProfile.get({
        sub: userSub,
      });

      if( errors || !data) {
        console.error("🔴 Failed to fetch specific user profile:", errors);
        return;
      }
      //console.log("*fetchSpecificUserProfile* Fetched user profile:", data);
      return data;
    } catch (error) {
      console.error("Failed to fetch specific user profile:", error);
    }
  }

  const fetchUserByUsername = async (username) => {
    try {
      const { data, errors } = await client.models.UserProfile.list({
        filter: {
          username: { eq: username }
        }
      });

      if (errors || !data?.length) {
        console.error("🔴 Failed to fetch specific user profile:", errors || "User not found: ", username);
        return;
      }

      const user = data[0];
      //console.log("*fetchUserByUsername* Fetched user profile:", user);
      return user;
    } catch (error) {
      console.error("Failed to fetch specific user profile:", error);
    }
  }

  const updateUserTokens = async () => {
    try {
      const user = await getCurrentUser();
      const { data: userProfileData, error } = await client.models.UserProfile.get(
        {sub: user.username},
        {
          selectionSet: ['tokens', 'lastDailyLogin'],
        }
      );

      if (error || !userProfileData) {
        //console.log("🔴 Failed to fetch profile", error);
        return;
      }
      //console.log("*updateUserTokens* Fetched user tokens:", userProfileData);

      setUserProfile(prev => ({
        ...prev,
        tokens: userProfileData.tokens,
        lastDailyLogin: userProfileData.lastDailyLogin,
      }));
    } catch (e) {
      //console.log("🔴 Failed to fetch profile", e);
    }
  }

  const updateUserSocials = async (socials) => {
    try {
      const user = await getCurrentUser();
      const { data, errors } = await client.models.UserProfile.update({
        sub: user.username,
        socials: {
          twitter: socials.twitter,
          instagram: socials.instagram,
          youtube: socials.youtube,
          twitch: socials.twitch,
          discord: socials.discord,
        }
      });

      if (errors) {
        //console.log("🔴 Failed to fetch profile", errors);
        return;
      }

      //console.log("*updateUserSocials* Updated user socials: ", data);
    } catch (e) {
      console.error("🔴 Failed to update user socials", e);
    }
  }

  const fetchUserArtworks = async () => {
    //setIsLoading(true);
    try {
      const { data: ownedArtwork } = await userProfile.ownedArtwork();
      //console.log("*fetchUserArtworks* Fetched user artworks:", ownedArtwork.length);
      setUserArtwork(ownedArtwork);
    } catch (error) {
      console.error("Failed to fetch artwork:", error);
      setUserArtwork([]); // fallback to empty
    } finally {
      //setIsLoading(false);
    }
  };

  const fetchUserArtworkMaskChunk = async () => {
    setIsLoading(true);
    try {
      const { data: ownedArtwork } = await userProfile.ownedArtwork();
      //console.log("*fetchUserArtworkMaskChunk* Fetched user ArtworkMaskChunk:", ownedArtwork.length);
      setUserArtwork(ownedArtwork);
    } catch (error) {
      console.error("Failed to fetch artwork:", error);
      setUserArtwork([]); // fallback to empty
    } finally {
      setIsLoading(false);
    }
  };

  const checkUsernameAvailability = async (name) => {
    const { data, errors } = await client.models.UserProfile.list({
      filter: { username: { eq: name } },
      limit: 1,
    });
    if (errors) console.warn("checkUsernameAvailability errors:", errors);
    return (data?.length ?? 0) === 0;
  };

  const setUsername = async (name) => {
    try {
      //console.log("setUsername: ", name);
      const response = await client.queries.changeUsername({
        username: name,
        authMode: 'userPool'
      });
      //console.log("*setUsername* Called username change:", response);

      await fetchUserProfile();
    } catch (error) {
      console.error("Error in setUsername:", error);
    }
  };

  const updateUserBannerConfig = async (from, to, angle) => {
    try {
      //console.log("*updateUserBannerConfig*: ", from, to, angle);

      const activeClient = await fetchClientMode();

      const response = await activeClient.models.UserProfile.update({
        sub: userProfile.sub,
        bannerConfig: {
          bannerFrom: from,
          bannerTo: to,
          bannerAngle: angle,
        }
      });
      //console.log("*updateUserBannerConfig* Called username change:", response);

      await fetchUserProfile();
    } catch (error) {
      console.error("Error in setUsername:", error);
    }
  };

  const updateSocials = async (socials) => {
    try {
      //console.log("*updateSocials*: ", socials);

      const activeClient = await fetchClientMode();

      const response = await activeClient.models.UserProfile.update({
        sub: userProfile.sub,
        socials, // { twitter, instagram, youtube, twitch, discord }
      });
      //console.log("*updateSocials* Called socials change:", response);

      await fetchUserProfile();
    } catch (error) {
      console.error("Error in updateSocials:", error);
    }
  };

  /*const fetchUserProfileAvatar = async (username) => {
    setIsLoading(true);
    try {
      const { identityId } = await fetchAuthSession();   // <-- this is the {entity_id}
      if (!identityId) throw new Error('No identityId; user not authenticated via Identity Pool');

      const { url } = await getUrl({
        path: `profile-pictures/${identityId}/${username}.png`,
      })
      
      setAvatarUrl(url.toString());

      //console.log('*fetchUserProfileAvatar* Fetched profile avatar:', url.toString());
    } catch (error) {
      console.error("Failed to fetch avatar:", error);
      setAvatarUrl(); // fallback to empty
    } finally {
      setIsLoading(false);
    }
  }*/

  const updateUserProfileAvatar = async (base64Png) => {
    try {
      const activeClient = await fetchClientMode();

      const response = await activeClient.models.UserProfile.update({
        sub: userProfile.sub,
        avatarImageData: base64Png,
      });
      //console.log("*updateUserProfileAvatar* Called username change:", response);

      await fetchUserProfile();
    } catch (error) {
      console.error("Error in setUsername:", error);
    }
  }

  /*function base64ToBlob(base64, mime = 'image/png') {
    // base64 is the **body only** (no "data:image/png;base64," prefix)
    const bin = atob(base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  const updateUserProfileAvatar = async (base64Png) => {
    try {
      const { identityId } = await fetchAuthSession();   // <-- this is the {entity_id}
      if (!identityId) throw new Error('No identityId; user not authenticated via Identity Pool');

      // Keep the format consistent with your editor output (PNG)
      const key = `profile-pictures/${identityId}/${userProfile.username}.png`;
      const blob = base64ToBlob(base64Png, 'image/png');

      const result = await uploadData({
        path: key,                         // <-- use key (not path)
        data: blob,
        options: {
          contentType: 'image/png',  // ensure correct type is stored
          // cacheControl: 'public, max-age=31536000, immutable', // optional
          expiresIn: 900
        },
      }).result;

      //console.log('*updateUserProfileAvatar* Succeeded:', result);
      
      const { url } = await getUrl({
        path: key,
        options: { expiresIn: 900 }, // optional: 15min
      });

      setAvatarUrl(url.toString());
    } catch (error) {
      //console.log('*updateUserProfileAvatar* Error:', error);
    }
  }*/

  const increaseExtendTime = async () => {
    try {
      const response = await client.queries.increaseExtendTime({
        authMode: 'userPool'
      });
      //console.log("*extendArtworkExpiresAt* Artwork ownership extended:", response);
      await fetchUserProfile(); // 👈 Refresh tokens/profile
      return response.data;
    } catch (error){
      console.error("*increaseExtendTime* Error increasing extend time: ", error);
    }
  }

  const increaseAvatarSize = async () => {
    try {
      const response = await client.queries.increaseAvatarSize({
        authMode: 'userPool'
      });
      //console.log("*increaseAvatarSize* Artwork ownership extended:", response);
      await fetchUserProfile(); // 👈 Refresh tokens/profile
      return response.data;
    } catch (error){
      console.error("*increaseAvatarSize* Error increasing avatar size: ", error);
    }
  }

  const increaseBuySize = async () => {
    try {
      const response = await client.queries.increaseBuySize({
        authMode: 'userPool'
      });
      //console.log("*increaseBuySize* Artwork ownership extended:", response);
      await fetchUserProfile(); // 👈 Refresh tokens/profile
      return response.data;
    } catch (error){
      console.error("*increaseBuySize* Error increasing avatar size: ", error);
    }
  }

  const increaseFreeDrawMaxPixels = async () => {
    try {
      const activeClient = await fetchClientMode();
      const response = await activeClient.queries.increaseFreeDrawMaxPixels({});
      if (response?.errors?.length) {
        console.error("*increaseFreeDrawMaxPixels* query errors:", response.errors);
        return response.errors[0]?.message ?? 'Unable to increase free draw pixel capacity.';
      }
      await fetchUserProfile();
      return response.data;
    } catch (error){
      console.error("*increaseFreeDrawMaxPixels* Error increasing free draw max pixels: ", error);
      return 'Unable to increase free draw pixel capacity.';
    }
  }

  const purchasePlaceToken = async (quantity = 1) => {
    try {
      const activeClient = await fetchClientMode();
      const response = await activeClient.queries.purchasePlaceToken({ quantity });
      if (response?.errors?.length) {
        console.error("*purchasePlaceToken* query errors:", response.errors);
        return response.errors[0]?.message ?? 'Unable to buy a place token.';
      }
      await fetchUserProfile();
      return response.data;
    } catch (error){
      console.error("*purchasePlaceToken* Error purchasing place token: ", error);
      return 'Unable to buy a place token.';
    }
  }

  const purchasePremiumColor = async (colorId) => {
    try {
      const activeClient = await fetchClientMode();
      const response = await activeClient.queries.purchasePremiumColor({ colorId });
      if (response?.errors?.length) {
        console.error("*purchasePremiumColor* query errors:", response.errors);
        return response.errors[0]?.message ?? 'Unable to buy this premium color.';
      }
      await fetchUserProfile();
      return response.data;
    } catch (error){
      console.error("*purchasePremiumColor* Error purchasing premium color: ", error);
      return 'Unable to buy this premium color.';
    }
  }

  const getFreeDrawStatus = async () => {
    try {
      const activeClient = await fetchClientMode();
      const { data, errors } = await activeClient.queries.getFreeDrawCooldownStatus({});
      const fallbackMaxPixels = userProfile?.sub ? BASE_FREE_DRAW_MAX_PIXELS : GUEST_FREE_DRAW_MAX_PIXELS;

      if (errors?.length) {
        console.error('*getFreeDrawStatus* errors:', errors);
        return { ok: false, availablePixels: 0, maxPixels: fallbackMaxPixels, missingPixels: 0, msUntilNextPixel: 0, placeTokensRemaining: 0 };
      }

      return data ?? { ok: false, availablePixels: 0, maxPixels: fallbackMaxPixels, missingPixels: 0, msUntilNextPixel: 0, placeTokensRemaining: 0 };
    } catch (error) {
      console.error('*getFreeDrawStatus* error:', error);
      return { ok: false, availablePixels: 0, maxPixels: userProfile?.sub ? BASE_FREE_DRAW_MAX_PIXELS : GUEST_FREE_DRAW_MAX_PIXELS, missingPixels: 0, msUntilNextPixel: 0, placeTokensRemaining: 0 };
    }
  }

  const usePlaceTokensForFreeDraw = async (tokenCount = 1) => {
    try {
      const activeClient = await fetchClientMode();
      const response = await activeClient.queries.usePlaceTokensForFreeDraw({ tokenCount });
      if (response?.errors?.length) {
        console.error("*usePlaceTokensForFreeDraw* query errors:", response.errors);
        return { ok: false, availablePixels: 0, maxPixels: BASE_FREE_DRAW_MAX_PIXELS, missingPixels: 0, msUntilNextPixel: 0, placeTokensRemaining: 0, message: response.errors[0]?.message ?? 'QUERY_ERROR' };
      }
      await fetchUserProfile();
      return response.data ?? { ok: false, availablePixels: 0, maxPixels: BASE_FREE_DRAW_MAX_PIXELS, missingPixels: 0, msUntilNextPixel: 0, placeTokensRemaining: 0 };
    } catch (error){
      console.error("*usePlaceTokensForFreeDraw* Error using place tokens for free draw: ", error);
      return { ok: false, availablePixels: 0, maxPixels: BASE_FREE_DRAW_MAX_PIXELS, missingPixels: 0, msUntilNextPixel: 0, placeTokensRemaining: 0, message: 'EXCEPTION' };
    }
  }
  // #endregion User Profile Management

  function createLocalCanvas({
    page,
    snapshotVersion,
    imageData,
    price = 1,
    latestKey,
    //size = 32,
    isEventCanvas = false,
    isActive = true,
    //shape = "o",
    dimensionX = 512,
    dimensionY = 512,
    tiles = []
  }) {
    return {
      page,
      isEventCanvas,
      isActive,
      snapshotVersion,
      imageData,
      price,
      latestKey,
      //size,
      //shape,
      dimensionX,
      dimensionY,
      tiles,
    };
  }

  // #region Canvas
  const fetchCanvas = async (page) => {
    setIsLoading(true);
    const activeClient = await fetchClientMode();
    const normalizedPage = normalizeCanvasPageName(page);
    try {
      const { data } = await activeClient.models.Canvas.get({
        page: normalizedPage
      });
      if(!data) {
        console.warn("*fetchCanvas* No canvas found for page:", normalizedPage);
        const fallbackCanvas = createLocalCanvas({ page: normalizedPage });
        setCanvas(fallbackCanvas);
        if (containsBlockedCanvasPageTerm(normalizedPage)) {
          console.warn("*fetchCanvas* Blocked profane canvas page from being auto-created:", normalizedPage);
          return;
        }
        try {
          const snapshotVersion = new Date().toISOString();
          const { data: createdCanvas, errors: createErrors } = await activeClient.models.Canvas.create({
            page: normalizedPage,
            isEventCanvas: false,
            isActive: true,
            snapshotVersion,
            imageData: "empty",
            price: 1,
            dimensionX: Number(fallbackCanvas.dimensionX ?? 512),
            dimensionY: Number(fallbackCanvas.dimensionY ?? 512),
          });

          if (createErrors?.length) {
            console.error("*fetchCanvas* Failed to create missing canvas record:", createErrors);
          } else if (createdCanvas) {
            setCanvas(createdCanvas);
          }
        } catch (createError) {
          console.error("*fetchCanvas* Failed to create missing canvas record:", createError);
        }
        return;
      }
      setCanvas(data);
      //console.log("*fetchCanvas* Fetched canvas for page:", data.page);
    } catch (error) {
      console.error("Failed to fetch canvas:", error);
      setCanvas(createLocalCanvas({ page: normalizedPage })); // not: setCanvas();
    } finally {
      setIsLoading(false);
    }
  }

  const listCanvases = async () => {
    const activeClient = await fetchClientMode();
    try {
      const [{ data, errors }, chunkResponse] = await Promise.all([
        activeClient.models.Canvas.list(),
        activeClient.models.FreeCanvasChunk.list({ limit: 2000 }),
      ]);

      if (errors) {
        console.error('Failed to list canvases:', errors);
        return [];
      }

      const chunkErrors = chunkResponse?.errors;
      if (chunkErrors) {
        console.error('Failed to list free draw chunks for activity ranking:', chunkErrors);
      }

      const lastActivityByPage = new Map();
      for (const chunk of (chunkResponse?.data ?? [])) {
        const page = String(chunk?.page ?? '');
        if (!page) continue;

        const updatedAtMs = Date.parse(String(chunk?.updatedAt ?? ''));
        if (!Number.isFinite(updatedAtMs)) continue;

        const currentMs = lastActivityByPage.get(page) ?? 0;
        if (updatedAtMs > currentMs) {
          lastActivityByPage.set(page, updatedAtMs);
        }
      }

      return (data ?? [])
        .filter((entry) => entry?.page && !entry?.isEventCanvas)
        .filter((entry) => !containsBlockedCanvasPageTerm(entry.page))
        .sort((a, b) => {
          const activityA = lastActivityByPage.get(String(a.page)) ?? 0;
          const activityB = lastActivityByPage.get(String(b.page)) ?? 0;

          if (activityA !== activityB) {
            return activityB - activityA;
          }

          return String(a.page).localeCompare(String(b.page));
        });
    } catch (error) {
      console.error('Failed to list canvases:', error);
      return [];
    }
  }


  const migrateAllCanvasesTo512 = async () => {
    const activeClient = await fetchClientMode();
    try {
      const canvases = await listCanvases();
      const updatedPages = [];

      for (const entry of canvases) {
        if (!entry?.page) continue;
        if (Number(entry.dimensionX) === 512 && Number(entry.dimensionY) === 512) continue;

        const { errors } = await activeClient.models.Canvas.update({
          page: entry.page,
          dimensionX: 512,
          dimensionY: 512,
        });

        if (errors?.length) {
          console.error('Failed to migrate canvas dimensions:', entry.page, errors);
          continue;
        }

        updatedPages.push(entry.page);
      }

      return updatedPages;
    } catch (error) {
      console.error('Failed to migrate canvases to 512x512:', error);
      return [];
    }
  }

  const updateCanvasDimensions = async (page, dimensionX, dimensionY) => {
    const activeClient = await fetchClientMode();
    try {
      const normalizedPage = normalizeCanvasPageName(page);
      const nextDimensionX = Number(dimensionX);
      const nextDimensionY = Number(dimensionY);

      if (!normalizedPage || !Number.isFinite(nextDimensionX) || !Number.isFinite(nextDimensionY)) {
        return { ok: false, message: 'Invalid canvas size request.' };
      }

      const { data: existingCanvas, errors: fetchErrors } = await activeClient.models.Canvas.get({
        page: normalizedPage,
      });

      if (fetchErrors?.length || !existingCanvas) {
        console.error('Failed to load canvas before resizing:', fetchErrors);
        return { ok: false, message: 'Canvas page not found.' };
      }

      const { data, errors } = await activeClient.models.Canvas.update({
        page: normalizedPage,
        dimensionX: nextDimensionX,
        dimensionY: nextDimensionY,
      });

      if (errors?.length) {
        console.error('Failed to update canvas dimensions:', errors);
        return { ok: false, message: 'Unable to update canvas size.' };
      }

      if (canvas?.page === normalizedPage && data) {
        setCanvas((prev) => ({
          ...(prev || existingCanvas),
          dimensionX: data.dimensionX,
          dimensionY: data.dimensionY,
        }));
      }

      return {
        ok: true,
        message: `Canvas ${normalizedPage} is now ${nextDimensionX}x${nextDimensionY}.`,
      };
    } catch (error) {
      console.error('Failed to update canvas dimensions:', error);
      return { ok: false, message: 'Unable to update canvas size.' };
    }
  }
  const fetchCanvasS3 = async (page) => {
    setIsLoading(true);
    const activeClient = await fetchClientMode();
    try {
      const { data, errors } = await activeClient.models.Canvas.get({
        page
      });

      if(!data) {
        console.warn("*fetchCanvasS3* No canvas found for page:", page);
        setCanvasUrl(); // fallback to empty
        setCanvas(createLocalCanvas({ page }));
        return;
      }
    
      if (errors) {
        console.error('Error fetching snapshot:', errors);
        return;
      }
      if (!data || !data.latestKey) {
        setCanvasUrl(); // fallback to empty
        setCanvas(data);
        console.warn('No snapshot data found.');
        return;
      }

      const { url } = await getUrl({
        path: data.latestKey,
      })
      
      setCanvas(data);
      setCanvasUrl(url.toString());

      //console.log('*fetchCanvasS3* Latest snapshot version:', data.snapshotVersion);
    } catch (error) {
      console.error("Failed to fetch canvas:", error);
      setCanvas(createLocalCanvas({ page }));
      setCanvasUrl(undefined);
    } finally {
      setIsLoading(false);
    }
  }

  /*const fetchLatestCanvasSnapshot = async (page) => {
    const { data, errors } = await client.models.Canvas.get({
      page
    });
  
    if (errors) {
      console.error('Error fetching snapshot:', errors);
      return;
    }

    if (!data || !data.imageData) {
      console.warn('No snapshot data found.');
      return;
    }
  
    //console.log('*fetchLatestCanvasSnapshot* Latest snapshot version:', data.snapshotVersion);
    return data.imageData;
  };*/

  const updateCanvasSnapshot = async (canvasSnapshot, page) => {
    try {
      const snapshotVersion = new Date().toISOString(); // or use Date.now()

      const { data, errors } = await client.models.Canvas.update({
        page,
        snapshotVersion,
        imageData: canvasSnapshot,
      });

      if (errors) {
        console.error("Failed to save canvas snapshot:", errors);
      } else {
        //console.log("*updateCanvasSnapshot* Canvas snapshot saved:", data);
      }
    } catch (error) {
      console.error("Error saving canvas snapshot:", error);
    }
  }

  const updateCanvasSnapshotS3 = async (canvasSnapshot, page) => {
    try {
      const snapshotVersion = new Date().toISOString(); // or use Date.now()

      const { data, errors } = await client.models.Canvas.update({
        page,
        snapshotVersion,
        latestKey: `pages/${page}/snapshots/${snapshotVersion}.jpg`,
      });

      const result = await uploadData({
        //path: "album/2024/1.jpg",
        path: `pages/${page}/snapshots/${snapshotVersion}.jpg`,
        data: canvasSnapshot,
      }).result;
      console.log('*updateCanvasSnapshotS3* Succeeded: ', result);
    } catch (error) {
      console.log('*updateCanvasSnapshotS3*  Error : ', error);
    }
  }

  const createCanvas = async (page, price = 1, dimensionX = 512, dimensionY = 512) => {
    try {
      const normalizedPage = normalizeCanvasPageName(page);
      if (!normalizedPage) {
        return { ok: false, message: 'Canvas page is required.' };
      }
      if (containsBlockedCanvasPageTerm(normalizedPage)) {
        return { ok: false, message: 'Canvas page name is not allowed.' };
      }

      const canvas = document.createElement("canvas");
      canvas.width = dimensionX;
      canvas.height = dimensionY;

      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; // white
      ctx.fillRect(0, 0, dimensionX, dimensionY);

      // 2. Convert canvas to base64 PNG
      const emptyCanvas = canvas.toDataURL("image/png");

      const snapshotVersion = new Date().toISOString();

      const { data, errors } = await client.models.Canvas.create({
        page: normalizedPage,
        isEventCanvas: false,
        snapshotVersion,
        imageData: "empty",
        price,
        dimensionX,
        dimensionY,
      })

      const result = await uploadData({
        //path: "album/2024/1.jpg",
        path: `pages/${normalizedPage}/snapshots/${snapshotVersion}.jpg`,
        data: emptyCanvas,
      }).result;

      if (errors) {
        console.error("Failed to save canvas snapshot:", errors);
        return { ok: false, message: 'Unable to create canvas.' };
      } else {
        //console.log("*createCanvas* Canvas created:", data, result);
        return { ok: true, data, result };
      }
    } catch (error) {
      console.error("Error creating canvas: ", error);
      return { ok: false, message: 'Unable to create canvas.' };
    }
  }
  // #endregion Canvas

  // #region Artwork
  const fetchArtwork = async (artworkId) => {
    try {
      const { data: artwork } = await client.models.Artwork.get({ 
        id: artworkId 
      });
      //console.log("*fetchArtwork* Fetched artwork:", artwork);
      return artwork;
    }
    catch (error) {
      console.error("Error fetching artwork:", error);
    }
  }

   const fetchArtworkWithSocials = async (artworkId) => {
    try {
      const { data: artwork } = await client.models.Artwork.get({ 
        id: artworkId 
      });
      const { data: owner } = await artwork.ownerProfile({
        selectionSet: ['socials.*']
      })
      //console.log("*fetchArtworkWithSocials* Fetched artwork:", artwork);
      return {
        ...artwork,
        ownerProfile: {
          ...(artwork.ownerProfile ?? {}),
          socials: owner.socials,
        },
      };
    }
    catch (error) {
      console.error("Error fetching artwork:", error);
    }
  }

  const fetchArtworkSilhouetteChunks = async (artworkId) => {
    try {
      const chunks = [];
      let nextToken = undefined;
      do {
        const res = await client.models.ArtworkMaskChunk.list({
          filter: { artworkId: { eq: artworkId } },
          limit: 200,
          nextToken,
        });
        //console.log("*fetchArtworkSilhouetteChunks* Fetched Silhouette:", res.data);
        if(res?.data) chunks.push(...res.data.filter(Boolean));
        nextToken = res?.nextToken ?? null;
      } while (nextToken)

      return chunks;
    }
    catch (error) {
      console.error("Error fetching artwork silhouette:", error);
    }
  }

  const fetchMaskChunksAt = async (page, chunkX, chunkY) => {
    try {
      const chunks = [];
      let nextToken = undefined;
      do {
        const res = await client.models.ArtworkMaskChunk.list({
          filter: {
            page: { eq: page },
            chunkX: { eq: chunkX },
            chunkY: { eq: chunkY },
          },
          limit: 200,
          nextToken,
        })
        //console.log("*fetchMaskChunksAt* Fetched Silhouettes:", res.data);
        if(res?.data) chunks.push(...res.data.filter(Boolean));
        nextToken = res?.nextToken ?? null;
      } while (nextToken)

      return chunks;
    }
    catch (error) {
      console.error("Error fetching artwork silhouette:", error);
    }
  }

  const saveArtwork = async (artworkId, imageDataUrl) => {
    try {
      const { data: artwork } = await client.models.Artwork.get({
        id: artworkId
      })

      if(!artwork) {
        console.error("*saveArtwork* Error fetching artwork.");
        return;
      }

      //console.log("*saveArtwork* Saving artwork:", imageDataUrl );
      await client.models.Artwork.update({
        id: artworkId,
        imageData: imageDataUrl,
      });
    } catch (error) {
      console.error("*saveArtwork* error saving artwork: ", error);
    }
  }

  const updateArtwork = async (artworkId, description, title) => {
    const { data: artwork} = await client.models.Artwork.get({
      id: artworkId
    });

    if(!artwork) {
      console.error("*updateArtwork* Error fetching artwork.");
      return;
    }

    await client.models.Artwork.update({
      id: artworkId,
      description,
      title,
    });

    //console.log("*updateArtwork* artwork updated:", { artworkId, description, title });
  }

  const deleteArtwork = async (artworkId) => {
    try {
      const { data: artwork} = await client.models.Artwork.get({
        id: artworkId
      });
      if(userProfile.sub !== artwork.ownerSub) {
        console.error("*deleteArtwork* You are not the owner of this artwork anymore.");
        return false;
      }
      await client.models.Artwork.delete({
        id: artworkId
      });
      //console.log("*deleteArtwork* Artwork deleted:", artworkId);
      return true;
    } catch (error) {
      console.error("*deleteArtwork* Error fetching artwork: ", error);
      return false;
    }
  }

  const extendArtworkExpiresAt = async (artworkId, hours) => {
    try {
      const response = await client.queries.extendArtworkOwnership({
        artworkId,
        hours,
        authMode: 'userPool'
      });
      //console.log("*extendArtworkExpiresAt* Artwork ownership extended:", response);
      await fetchUserProfile(); // 👈 Refresh tokens/profile
      return response.data;
    } catch (error){
      console.error("*extendArtworkExpiresAt* Error fetching artwork: ", error);
    }
  }

  const refreshArtworkExpiresAt = async (artworkId) => {
    try {
      const response = await client.queries.refreshArtworkOwnership({
        artworkId,
        authMode: 'userPool'
      });
      //console.log("*refreshArtworkExpiresAt* Artwork ownership extended:", response);
      return response.data;
    } catch (error){
      console.error("*refreshArtworkExpiresAt* Error fetching artwork: ", error);
    }
  }
  
  // #endregion Artwork

  // #region Pixel

  const buyPixelBatch = async (page, selections, hours) => {
    try {
      const { data: canvas } = await client.models.Canvas.get({
        page
      });
      if(!canvas) {
        console.error("*buyPixelBatch* No canvas found for page:", page);
        await createCanvas(page, 1, 512, 512); // Default values
      }
      const response = await client.queries.buyPixelBatch({
        //selections,
        selectionsJson: JSON.stringify(selections),
        hours,
        page,
        authMode: 'userPool'
      });

      //console.log("*buyPixelBatch* Pixels purchased:", response);
      await fetchUserProfile(); // 👈 Refresh tokens/profile
      return response.data;
    } catch (error) {
      console.error("Error in buyPixelBatch:", error);
    }
  }

  const submitFreeDraw = async (page, touchedPixelsJson) => {
    try {
      const response = await client.queries.validateFreeDraw({
        page,
        touchedPixelsJson,
        authMode: 'identityPool',
      });
      
      if(!response.data) {
        return false;
      }

      console.log("*submitFreeDraw* Free draw submitted:", response);
      return true;
    } catch (error) {
      console.error("Error in submitFreeDraw:", error);
    }
  }

  

  const placeFreePixel = async ({ page, row, column, color }) => {
    try {
      const activeClient = await fetchClientMode();
      const { data, errors } = await activeClient.queries.placeFreePixel({
        page,
        row,
        column,
        color,
      });

      if (errors?.length) {
        console.error('*placeFreePixel* errors:', errors);
        return { ok: false, reason: 'ERROR', availablePixels: 0, maxPixels: userProfile?.sub ? BASE_FREE_DRAW_MAX_PIXELS : GUEST_FREE_DRAW_MAX_PIXELS, missingPixels: 0, msUntilNextPixel: 0 };
      }

      return data ?? { ok: false, reason: 'NO_RESPONSE', availablePixels: 0, maxPixels: userProfile?.sub ? BASE_FREE_DRAW_MAX_PIXELS : GUEST_FREE_DRAW_MAX_PIXELS, missingPixels: 0, msUntilNextPixel: 0 };
    } catch (error) {
      console.error('Error in placeFreePixel:', error);
      return { ok: false, reason: 'EXCEPTION', availablePixels: 0, maxPixels: userProfile?.sub ? BASE_FREE_DRAW_MAX_PIXELS : GUEST_FREE_DRAW_MAX_PIXELS, missingPixels: 0, msUntilNextPixel: 0 };
    }
  }
  // #endregion Pixel

  // #region FreeDraw
  const CHUNK_SIZE = 64;
  const BYTES_PER_CHUNK = CHUNK_SIZE * CHUNK_SIZE * 4;

  const rgbaBase64ToBytes = (b64) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  };

  const bytesToRgbaBase64 = (bytes) => {
    let s = "";
    const chunk = 0x8000; // avoid callstack limits
    for (let i = 0; i < bytes.length; i += chunk) {
      s += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(s);
  };

  const hexToRgba = (hex) => {
    // supports #RRGGBB only (what your picker returns)
    const h = hex.startsWith("#") ? hex.slice(1) : hex;
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 255];
  };

  const fetchChunks = async (page) => {
    setIsLoading(true);
    const activeClient = await fetchClientMode();
    try {
      const { data, errors } = await activeClient.models.FreeCanvasChunk.list({
        filter: { page: { eq: page } }
      });

      if (errors) {
        console.error("Failed to fetch chunks:", errors);
        setChunks([]); // fallback to empty
        return;
      }

      if(!data) {
        console.warn("*fetchChunks* No chunks found for page:", page);
        setChunks([]); // fallback to empty
        return;
      }
      setChunks(data);
      console.log("*fetchChunks* Fetched chunks for page:", data.page, "count:", data.length);
    } catch (error) {
      console.error("Failed to fetch chunks:", error);
      setChunks([]); // fallback to empty
      return;
    } finally {
      setIsLoading(false);
    }
  }

  const placePixel = async (page, chunkId, index, color) => {
    try {
      const existing = await client.models.FreeCanvasChunk.get({
        page,
        chunkId
      })

      console.log("*placePixel* existing chunk:", existing?.data);

      // 1) Load bytes for chunk (existing or blank white)
      let bytes;
      if (existing?.data?.pixels) {
        bytes = rgbaBase64ToBytes(existing.data.pixels);
        // Safety: if older data was wrong length, reset
        if (bytes.length !== BYTES_PER_CHUNK) {
          bytes = new Uint8Array(BYTES_PER_CHUNK);
          bytes.fill(255); // white
        }
      } else {
        bytes = new Uint8Array(BYTES_PER_CHUNK);
        bytes.fill(255); // white
      }
      // 2) Modify 1 pixel
      const [r, g, b, a] = hexToRgba(color);
      const o = index * 4;
      bytes[o] = r;
      bytes[o + 1] = g;
      bytes[o + 2] = b;
      bytes[o + 3] = a;

      // 3) Encode and upsert
      const pixelsB64 = bytesToRgbaBase64(bytes);

      if (existing?.data) {
        await client.models.FreeCanvasChunk.update({
          page,
          chunkId,
          pixels: pixelsB64,
        });
      } else {
        try {
          await client.models.FreeCanvasChunk.create({
            page,
            chunkId,
            pixels: pixelsB64,
          });
        } catch (e) {
          // race: someone created chunk first, so update instead
          await client.models.FreeCanvasChunk.update({
            page,
            chunkId,
            pixels: pixelsB64,
          });
        }
      }
      console.log("*placePixel* ok", { page, chunkId, index, color });
    } catch (error) {
      console.error("Error in placePixel:", error);
    }
  }

  //endregion FreeDraw

  // #region Messages
  const subscribeToMessages = async ({ page } = {}) => {
    //console.log("*subscribeToMessages* Subscribing to messages for page:", page);

    //const filter = page ? { filter: { page: { eq: page } } } : undefined;
    
    const activeClient = await fetchClientMode();

    const updateSub = activeClient.models.Message.onCreate().subscribe({
      next: (evt) => {
        const rec = evt?.data ?? evt;
        // If caller passed a page, only emit messages matching that page
        // Otherwise (global), only emit messages without a page
        if (page) {
          if (rec?.page === page) updateChat(rec, "Chat");
        } else {
          if (!rec?.page) updateChat(rec, "Chat");
        }
      },
      error: (err) => console.error('[Message subscription] error:', err),
      owner: fetchUserAttributes.subscribe,
      authMode: 'identityPool',
    });
    
    return () => updateSub.unsubscribe();
  }

  const subscribeToChat = (callback) => {
    chatSubscribers.add(callback);
    return () => chatSubscribers.delete(callback); // unsubscribe cleanup
  };

  const updateChat = (data, kind) => {
    //Send a notification to the canvas with the tile row/column that got updated to update it locally
    const record = data.data ?? data; // Amplify subs usually put the row on .data
    chatSubscribers.forEach((cb) => cb({ kind, record }));
  }
  
  const createMessage = async (newMessage) => {
    try {
      //console.log("*createMessage* Sending message:", newMessage);
      const activeClient = await fetchClientMode();

      const {data, errors} = await activeClient.models.Message.create({
        username: newMessage.username,
        content: newMessage.content,
        page: newMessage.page,
        timestamp: new Date().toISOString(),
        expiresAt: Math.floor((Date.now() + 24 * 60 * 60 * 1000) / 1000), // 1 day later in epoch seconds
      })

      if (errors) {
        console.error("Error sending message:", errors);
        return;
      }
    } catch (error) {
      console.error("Failed to create message:", error);
    }
  }

  const fetchRecentMessages = async ({ scope, page }) => {
    //console.log("*fetchRecentMessages* fetching recent messages for scope:", scope, "page:", page);
    const activeClient = await fetchClientMode();
    const tenMinAgoISO = new Date(Date.now() - 10 * 60 * 1000).toISOString();

    // Get all messages from last 10 mins (server-side filter by time; cheap + reliable).
    // We’ll scope to global/page on the client side to avoid tricky null filtering in GraphQL.
    const res = await activeClient.models.Message.list({
      filter: { timestamp: { ge: tenMinAgoISO } },
      limit: 500, // adjust as needed; you can paginate if you expect >500 in 10 mins
    });

    const raw = res?.data ?? [];

    // Scope to tab locally:
    const filtered =
      scope === 'global'
        ? raw.filter((m) => !m.page)            // global = no page
        : raw.filter((m) => m.page === page);   // page = exact page match

    // Sort ascending so UI renders top->bottom chronologically
    filtered.sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));

    return filtered;
  };
  // #endregion Messages

  // #region News & Rewards
  const updateUserLastSeenNews = async () => {
    try {
      const now = new Date().toISOString();
      const { data, errors } = await client.models.UserProfile.update({
        sub: userProfile.sub,
        lastSeenNewsAt: now,
      });
    
      if (errors) {
        console.error("Failed to update lastSeenNewsAt:", errors);
      } else {
        //console.log("*updateUserLastSeenNews* lastSeenNewsAt updated successfully:", data);
        setUserProfile(data); // update local state
      }
    } catch (error) {
      console.error("Error in updateUserLastSeenNews:", error);
    }
  }

  const fetchLatestNews = async () => {
    try {
      const { data, errors } = await client.models.News.list({
        sort: {
          field: 'timestamp',
          direction: SortDirection.DESCENDING
        },
      });

      if (errors) {
        console.error("Failed to fetch latest news:", errors);
        return null;
      }

      if (data.length === 0) {
        console.warn("No news found.");
        return null;
      }
      for(const news of data) {
        //console.log("*fetchLatestNews* Latest news:", news);
      }
      return data;
    } catch (error) {

    }
  }

  const createNews = async (title, description, content) => {
    const timestamp = new Date().toISOString();
    try {
      const { data, errors } = await client.models.News.create({
        id: title,
        title,
        description,
        content,
        timestamp,
      },
      {
        authMode: 'userPool'
      })

      if(errors) {
        //console.log("*createNews* failed to create news:", errors)
      }

      //console.log("*createNews* news created:", data);
    } catch (error) {
      console.error("Failed to create news:", error)
    }
  }

  const checkDailyReward = () => {
    function getLastDailyReset(now = new Date()) {
      const reset = new Date(now);
      reset.setUTCHours(17, 0, 0, 0); // Reset to 5 PM today
      if (now < reset) {
        // If current time is before 5 PM, reset is yesterday 5 PM
        reset.setDate(reset.getDate() - 1);
      }
      return reset;
    }
    try {
      const now = new Date();
      const lastReset = getLastDailyReset(now); 
      const lastClaimed = userProfile.lastDailyLogin ? new Date(userProfile.lastDailyLogin) : new Date(0);
      //console.log("*checkDailyReward* Last daily reset at: " + lastReset + ", Last claimed at: " + lastClaimed); 
      
      if (lastClaimed < lastReset) {
        return true; // Show modal if last claimed is before today's reset
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error in checkDailyReward:", error);
    }
  }

  const claimDailyReward = async () => {
    try {
      const response = await client.queries.claimDailyReward({
        authMode: 'userPool'
      });

      if (response.data) {
        //console.log("*checkDailyReward* Daily reward data:", response);
      }
      
      await updateUserTokens(); // 👈 Refresh tokens/profile
      return response.data;
    } catch (error) {
      console.error("Error in checkDailyReward:", error);
    }
  }
  // #endregion News & Rewards

  // #region Events
  const createEvent = async(startTime, endTime, theme, type, description) => {
    setIsLoading(true);
    //console.log(startTime);
    try {
      const { data, errors } = await client.models.Event.create({
        eventId: "123",
        theme,
        startTime,
        endTime,
        page: theme,
        type,
        description
      });

      if(errors) {
        //console.log("creation of event failed:", errors);
      }

      //console.log("*createEvent* event created:", data);
    } catch (error) {
      console.error("Failed to create event:", error)
    } finally {
      setIsLoading(false);
    }
  }

  const fetchLastestEvent = async() => {
    setIsLoading(true);
    try {
      const now = new Date().toISOString(); // current time in ISO format

      const result = await client.models.Event.list({
        filter: {
          startTime: { gt: now },
        },
        sort: {
          field: 'startTime',
          direction: 'asc',
        },
        limit: 1,
      });

      const soonestEvent = result.data[0];
      setCurrentEvent(soonestEvent);

      //console.log("*fetchLatestEvent* Fetched event:", soonestEvent);
    } catch (error) {
      console.error("Failed to fetch event:", error)
    } finally {
      setIsLoading(false);
    }
  }
  // #endregion Events

  // #region EventCanvas
  /*

  const fetchLatestEventCanvasSnapshot = async (page) => {
    const { data, errors } = await client.models.EventCanvas.get({
      page
    });
  
    if (errors) {
      console.error('Error fetching snapshot:', errors);
      return;
    }

    if (!data || !data.imageData) {
      console.warn('No snapshot data found.');
      return;
    }
  
    //console.log('*fetchLatestEventCanvasSnapshot* Latest snapshot version:', data.snapshotVersion);
    return data.imageData;
  };

  const updateEventCanvasSnapshot = async (canvasSnapshot, page) => {
    try {
      const snapshotVersion = new Date().toISOString(); // or use Date.now()

      const { data, errors } = await client.models.EventCanvas.update({
        page,
        snapshotVersion,
        imageData: canvasSnapshot,
      });

      if (errors) {
        console.error("Failed to save canvas snapshot:", errors);
      } else {
        //console.log("*updateEventCanvasSnapshot* Canvas snapshot saved:", data);
      }
    } catch (error) {
      console.error("Error saving canvas snapshot:", error);
    }
  }*/
  const fetchEventCanvas = async() => {
    //setIsLoading(true);
    try {
      const { data } = await client.models.Canvas.get({
        //TODO: add event topic page...whatevs
        page: currentEvent.page,
      });
      setEventCanvas(data);
      //console.log("*fetchEventCanvas* Fetched canvas for event:", data);
    } catch (error) {
      console.error("Failed to fetch canvas:", error);
      setEventCanvas(); // fallback to empty
    } finally {
      //setIsLoading(false);
    }
  }

  const createEventCanvas = async (page, price, size, dimensionX, dimensionY) => {
    try {
      const normalizedPage = normalizeCanvasPageName(page);
      if (!normalizedPage) {
        return { ok: false, message: 'Canvas page is required.' };
      }
      if (containsBlockedCanvasPageTerm(normalizedPage)) {
        return { ok: false, message: 'Canvas page name is not allowed.' };
      }

      const canvas = document.createElement("canvas");
      canvas.width = dimensionX;
      canvas.height = dimensionY;

      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; // white
      ctx.fillRect(0, 0, dimensionX, dimensionY);

      // 2. Convert canvas to base64 PNG
      const emptyCanvas = canvas.toDataURL("image/png");

      const snapshotVersion = new Date().toISOString();

      const { data, errors } = await client.models.Canvas.create({
        page: normalizedPage,
        isEventCanvas: true,
        isActive: true,
        snapshotVersion,
        imageData: emptyCanvas,
        price,
        size,
        dimensionX,
        dimensionY,
      })

      if (errors) {
        console.error("Failed to save canvas snapshot:", errors);
        return { ok: false, message: 'Unable to create event canvas.' };
      } else {
        //console.log("*createEventCanvas* Canvas created:", data);
        return { ok: true, data };
      }
    } catch (error) {
      console.error("Error creating canvas: ", error);
      return { ok: false, message: 'Unable to create event canvas.' };
    }
  }
  // #endregion EventCanvas

  // #region STORE
  const createStripePaymentIntent = async (packId) => {
    try {
      console.log("Calling with packId:", packId);
      const result = await client.queries.createStripePaymentIntent({
        packId: packId,
        authMode: 'userPool'
      })
      console.log("*createStripePaymentIntent* Created payment intent for pack:", packId, result.data);

      return result.data;
    } catch (error) {
      console.error("Error creating Stripe payment intent:", error);
    }
  };


  // #endregion STORE

  const checkIfUserHasLikedArtwork = async (artworkId) => {
    try {
      const { data, errors} = await client.models.ArtworkLike.get({
        artworkId,
        userSub: userProfile.sub,
      });
      if (!data) {
        //console.log("*checkIfUserHasLikedArtwork* User has not liked this artwork yet.");
        return false; // User has not liked this tile
      }else {
        //console.log("*checkIfUserHasLikedArtwork* User has liked this artwork:", data);
        return true; // User has liked this tile
      }
    } catch (error) {
      console.error("Error checking if user has liked artwork:", error);
    }
  }

  const likeArtwork = async (artworkId) => {
    try {
      const response = await client.queries.likeArtwork({
        artworkId,
        authMode: 'userPool'
      });

      if(response?.data?.message === 'Already liked') {
        //console.log("*likeArtwork* You have already liked this artwork.");
      } else {
        //console.log("*likeArtwork* Artwork liked successfully:", response.data);
      }
    } catch (error) {
      console.error("Error liking artwork:", error);
    }
  };

  const updatePresence = async (isOnline, context) => {
    try {
      const activeClient = await fetchClientMode();
      const session = await fetchAuthSession();
      const identityId = session?.identityId || null;
      const presenceId = userProfile?.sub || identityId;

      if (!presenceId) {
        return;
      }

      const presencePayload = {
        userSub: presenceId,
        username: userProfile?.username || 'Guest',
        isOnline,
        context,
        lastSeen: new Date().toISOString(),
      };

      const { data, errors } = await activeClient.models.UserPresence.update(presencePayload);

      if (errors || !data) {
        await activeClient.models.UserPresence.create(presencePayload);
      }
    } catch (error) {
        console.error("Error updating user presence: ", error);
    }
  }

  const fetchOnlineUsers = async (context = null) => {
    try {
      const activeClient = await fetchClientMode();
      if(!context) {
        const { data, errors } = await activeClient.models.UserPresence.list({
          filter: {
            isOnline: { eq: true }
          },
        });

        //console.log("*fetchOnlineUsers* Online users in context:", context, data);
        return data;
      }else {
        const { data, errors } = await activeClient.models.UserPresence.list({
          filter: {
            context: { eq: context },
            isOnline: { eq: true }
          },
        });

        //console.log("*fetchOnlineUsers* Online users in context:", context, data);
        return data;
      }
    } catch (error) {
        console.error("Error fetching user presence: ", error);
    }
  }

  const fetchUserHighlights = async (userSub) => {
    try {
      const response = await client.models.UserHighlightArtwork.list({
        filter: {
          userSub: { eq: userSub }
        },
      });
      return response?.data ?? [];
    } catch (error) {
      console.error("Error fetching user highlights:", error);
    }
  }

  const setMyHighlights = async (artworkIds) => {
    try {
      const result = await client.queries.setUserHighlight({
        artworkIds,
        authMode: 'userPool'
      });
      //console.log("*setMyHighlights* Set user highlights:", result.data);
      
      return result?.data ?? [];
    } catch (error) {
      console.error("Error setting user highlights:", error);
    }
  }

  const createReport = async (artworkId) => {
    try {
      const { data: artwork } = await client.models.Artwork.get({
        id: artworkId
      });

      if(!artwork) {
        console.error("*createReport* Error fetching artwork.");
        return;
      }

      const { data, errors } = await client.models.ReportedArtwork.create({
        artworkId: artworkId,
        reporterSub: userProfile.sub?? "N/A",
        reportedSub: artwork.ownerSub,
        artworkImageData: artwork.imageData,
        artworkTitle: artwork.title,
        artworkDescription: artwork.description,
      })

      if (errors) {
        console.error("Failed to save canvas snapshot:", errors);
      } else {
        //console.log("*createEventCanvas* Canvas created:", data);
      }
    } catch (error) {
      console.error("Error creating canvas: ", error);
    }
  }

  const callHumanVerify = async (tsToken) => {
    try {
      const activeClient = await fetchClientMode();
      const { data, errors } = await activeClient.queries.humanVerify({
        tsToken,
      });
      if (errors) {
        console.error("*callHumanVerify* errors:", errors);
        return { ok: false, errorCodes: ["appsync-error"] };
      }
      return data; // { ok, errorCodes }
    } catch (error) {
      console.error("*callHumanVerify* exception:", error);
      return { ok: false, errorCodes: ["exception"] };
    }
  }

  const createSupportCase = async (supportCase) => {
    try {
      const activeClient = await fetchClientMode();
      const { data, errors } = await activeClient.models.SupportCase.create({
        subject: supportCase.subject,
        category: supportCase.category,
        message: supportCase.message,
        userSub: userProfile.sub,
        email: userProfile.email
      })

      if (errors) {
        console.error("*createSupportCase*Failed to create support case:", errors);
        return false;
      } else {
        //console.log("*createSupportCase* Canvas created:", data);
        return true;
      }
    } catch (error) {
      console.error("*createSupportCase*Failed to create support case: ", error);
      return false;
    }
  }

  const isTextBanned = async (text, reason = "generic") => {
    if (!text || typeof text !== "string") {
      return { banned: false, reasons: [], code: "EMPTY" };
    }

    try {
      const activeClient = await fetchClientMode();
      const { data, errors } = await activeClient.queries.validateText({
        text,
        reason,
      });

      // Handle Amplify API-level errors
      if (errors?.length) {
        console.error("*isTextBanned* validateText errors:", errors);
        return { banned: false, reasons: ["Server error"], code: "ERROR" };
      }

      if (!data) {
        console.warn("*isTextBanned* No data returned from validateText");
        return { banned: false, reasons: ["No data"], code: "NO_RESPONSE" };
      }
      //console.log("*isTextBanned* validateText result:", data);
      const isBanned = !data.ok;
      const reasons = data.reasons ?? [];
      const code = data.code ?? (isBanned ? "NSFW_TEXT" : "OK");

      return { banned: isBanned, reasons, code };
    } catch (err) {
      console.error("*isTextBanned* Exception:", err);
      return { banned: false, reasons: ["Request failed"], code: "EXCEPTION" };
    }
  };

  return(
    <BackendContext.Provider value={{ 
      //Canvas Subscriptions
      //subscribeToCanvasTileUpdates,
      //subscribeToCanvasPixelUpdates,
      subscribeToCanvasArtworkMaskChunkUpdates,
      subscribeToCanvasArtworkUpdates,
      subscribeToFreeCanvasChunkUpdates,
      subscribeToCanvasUpdatesLocal, 

      //User Profile Management
      updateUserTokens,
      userProfile,
      //avatarUrl,
      fetchSpecificUserProfile,
      fetchUserByUsername,
      updateUserSocials,
      isUserLoading,
      userTiles,
      fetchUserProfile,
      clearUserState,
      fetchUserArtworks,
      checkUsernameAvailability,
      setUsername,
      updateUserBannerConfig,
      updateSocials,
      //fetchUserProfileAvatar,
      updateUserProfileAvatar,
      increaseExtendTime,
      increaseAvatarSize,
      increaseBuySize,
      increaseFreeDrawMaxPixels,
      purchasePlaceToken,
      purchasePremiumColor,
      getFreeDrawStatus,
      usePlaceTokensForFreeDraw,

      //Canvas
      canvas,
      updateCanvasSnapshot, 
      createCanvas,
      fetchCanvas,
      listCanvases,
      migrateAllCanvasesTo512,
      updateCanvasDimensions,
      fetchCanvasS3,
      canvasUrl,
      updateCanvasSnapshotS3,

      //Artwork
      fetchArtwork,
      fetchArtworkWithSocials,
      saveArtwork,
      updateArtwork,
      fetchArtworkSilhouetteChunks,
      fetchMaskChunksAt,
      extendArtworkExpiresAt,
      refreshArtworkExpiresAt,
      deleteArtwork,
      userArtwork,

      //Pixel
      buyPixelBatch,
      submitFreeDraw,
      placeFreePixel,

      //FreeDraw
      fetchChunks,
      placePixel,
      chunks,

      //Messages
      subscribeToMessages,
      subscribeToChat,
      createMessage,
      fetchRecentMessages,

      //News & Rewards
      updateUserLastSeenNews,
      fetchLatestNews,
      createNews,
      checkDailyReward,
      claimDailyReward,

      //Events
      fetchLastestEvent,
      currentEvent,
      createEvent,

      //EventCanvas
      fetchEventCanvas,
      createEventCanvas,
      eventCanvas,

      //STORE
      createStripePaymentIntent,

      isAdmin,
      isLoading, 
      setIsLoading,

      updatePresence,
      fetchOnlineUsers,
      checkIfUserHasLikedArtwork,
      likeArtwork,
      fetchUserHighlights,
      setMyHighlights,
      createReport,
      callHumanVerify,
      createSupportCase,
      isTextBanned,
    }}>
      {children}
    </BackendContext.Provider>
  );
};


